import { Bell, Check, Download, Layers, LogIn, LogOut, Settings as SettingsIcon, Sparkles, Trash2, Unplug, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import type { AppUser, CodexAccount, CodexModelsResponse, LangPreference, OpenTab, SummaryLang, TranscriptMode } from '../../shared/api';
import { SUMMARY_LANGS } from '../../shared/lang';
import type { NotificationState } from '../lib/pwa';
import type { Settings } from '../lib/storage';
import { APP_NAME, LANG_NAMES, effortName, planLabel } from '../lib/texts';
import { Popover } from './Popover';
import { TabList, type TabStatus } from './TabList';

export interface CodexStatus {
  connected: boolean;
  account?: CodexAccount | undefined;
  /** Where the login is stored. */
  location?: 'server' | 'browser' | undefined;
}

export interface HeaderProps {
  user: AppUser | null;
  /** /api/me has not answered yet. */
  loading: boolean;
  codex: CodexStatus;
  settings: Settings;
  historyCount: number;
  tabs: OpenTab[];
  /** The video shown right now. */
  currentVideoId: string | undefined;
  tabStatus: (videoId: string) => TabStatus;
  onOpenTab: (tab: OpenTab) => void;
  onCloseTab: (tab: OpenTab) => void;
  onCloseAllTabs: () => void;
  /** The browser offers to install the app. */
  canInstall: boolean;
  /** Browser permission for "summary ready" notifications. */
  notifications: NotificationState;
  onEnableNotifications: () => void;
  onInstall: () => void;
  onSettingsChange: (patch: Partial<Settings>) => void;
  /** The models of the connected ChatGPT account. */
  onLoadModels: () => Promise<CodexModelsResponse>;
  onClearHistory: () => void;
  onConnectCodex: () => void;
  onDisconnectCodex: () => void;
  onSignIn: () => void;
  onSignOut: () => void;
  onHome: () => void;
}

export function Header(props: HeaderProps) {
  const { user, loading, codex, onHome } = props;
  return (
    <header className="topbar">
      <a
        className="brand"
        href="/"
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          onHome();
        }}
      >
        <img className="brand__logo" src="/icon.svg" width={32} height={32} alt="" />
        <span className="brand__name">{APP_NAME}</span>
      </a>
      <nav className="topbar__actions" aria-label="Tabs, Konto und Einstellungen">
        {!loading && <CodexPill {...props} codex={codex} />}
        {props.tabs.length > 0 && <TabsMenu {...props} />}
        <SettingsMenu {...props} />
        {!loading &&
          (user ? (
            <AccountMenu user={user} onSignOut={props.onSignOut} />
          ) : (
            <button type="button" className="btn btn--ghost btn--sm topbar__signin" onClick={props.onSignIn}>
              <LogIn size={17} aria-hidden="true" />
              <span>Anmelden</span>
            </button>
          ))}
      </nav>
    </header>
  );
}

function CodexPill({ codex, onConnectCodex, onDisconnectCodex }: HeaderProps) {
  const plan = planLabel(codex.account?.plan);
  return (
    <Popover
      label="ChatGPT-Verbindung"
      trigger={(triggerProps) => (
        <button
          type="button"
          {...triggerProps}
          className={`pill${codex.connected ? ' is-connected' : ''}`}
          aria-label={codex.connected ? `ChatGPT verbunden${plan ? ` (${plan})` : ''}` : 'ChatGPT nicht verbunden'}
        >
          <span className="pill__dot" aria-hidden="true" />
          <span className="pill__text pill__text--long">
            {codex.connected ? `ChatGPT verbunden${plan ? ` (${plan})` : ''}` : 'Nicht verbunden'}
          </span>
          <span className="pill__text pill__text--short">{codex.connected ? (plan ?? 'ChatGPT') : 'ChatGPT'}</span>
        </button>
      )}
    >
      {(close) =>
        codex.connected ? (
          <div className="menu">
            <p className="menu__title">Mit ChatGPT verbunden</p>
            <dl className="menu__facts">
              {codex.account?.email && (
                <>
                  <dt>Konto</dt>
                  <dd>{codex.account.email}</dd>
                </>
              )}
              {plan && (
                <>
                  <dt>Abo</dt>
                  <dd>{plan}</dd>
                </>
              )}
              <dt>Gespeichert</dt>
              <dd>{codex.location === 'server' ? 'in deinem Konto' : 'in diesem Browser'}</dd>
            </dl>
            <button
              type="button"
              className="btn btn--secondary btn--sm btn--block"
              onClick={() => {
                close();
                onDisconnectCodex();
              }}
            >
              <Unplug size={17} aria-hidden="true" />
              Verbindung trennen
            </button>
          </div>
        ) : (
          <div className="menu">
            <p className="menu__title">ChatGPT nicht verbunden</p>
            <p className="menu__text">
              Neue Zusammenfassungen erstellt ChatGPT mit deinem eigenen Abo. Bereits erstellte Zusammenfassungen
              kannst du auch ohne Verbindung lesen.
            </p>
            <button
              type="button"
              className="btn btn--primary btn--sm btn--block"
              onClick={() => {
                close();
                onConnectCodex();
              }}
            >
              <Sparkles size={17} aria-hidden="true" />
              Mit ChatGPT verbinden
            </button>
          </div>
        )
      }
    </Popover>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<readonly [T, string]>;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="segmented">
      <legend className="segmented__legend">{label}</legend>
      <div className="segmented__track">
        {options.map(([optionValue, optionLabel]) => (
          <label key={optionValue} className={`segmented__option${value === optionValue ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name={label}
              value={optionValue}
              checked={value === optionValue}
              onChange={() => onChange(optionValue)}
            />
            <span>{optionLabel}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const MODE_OPTIONS = [
  ['server', 'Server'],
  ['client', 'Browser'],
] as const satisfies ReadonlyArray<readonly [TranscriptMode, string]>;

/** "Deutsch", "Deutsch oder Englisch", "Deutsch, Englisch oder Spanisch" */
function orList(langs: readonly SummaryLang[]): string {
  const names = langs.map((lang) => LANG_NAMES[lang]);
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} oder ${names.at(-1)}`;
}

export function langPreferenceHint(pref: LangPreference): string {
  const fallback = LANG_NAMES[pref.fallback];
  const kept = pref.keep.filter((lang) => lang !== pref.fallback);
  if (kept.length === 0) return `Alle Videos werden auf ${fallback} zusammengefasst.`;
  return `Videos auf ${orList(pref.keep)} bleiben in ihrer Sprache, alle anderen werden auf ${fallback} zusammengefasst.`;
}

/** Languages the user reads (summaries stay in the video's language) + the language for all others. */
function LanguageSettings({ value, onChange }: { value: LangPreference; onChange: (value: LangPreference) => void }) {
  const fallbackId = useId();
  const toggle = (lang: SummaryLang) => {
    const keep = value.keep.includes(lang) ? value.keep.filter((l) => l !== lang) : [...value.keep, lang];
    onChange({ ...value, keep: SUMMARY_LANGS.filter((l) => keep.includes(l)) });
  };
  return (
    <div className="lang-pref">
      <fieldset className="lang-pref__keep">
        <legend className="segmented__legend">Sprachen, die du liest</legend>
        <div className="chips">
          {SUMMARY_LANGS.map((lang) => {
            const checked = value.keep.includes(lang);
            return (
              <label key={lang} className={`chip${checked ? ' is-selected' : ''}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(lang)} />
                <span className="chip__check" aria-hidden="true">
                  {checked && <Check size={13} strokeWidth={3} />}
                </span>
                <span>{LANG_NAMES[lang]}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      <div className="lang-pref__fallback">
        <label htmlFor={fallbackId} className="lang-pref__label">
          Alle anderen Videos auf
        </label>
        <select
          id={fallbackId}
          className="select"
          value={value.fallback}
          onChange={(event) => onChange({ ...value, fallback: event.target.value as SummaryLang })}
        >
          {SUMMARY_LANGS.map((lang) => (
            <option key={lang} value={lang}>
              {LANG_NAMES[lang]}
            </option>
          ))}
        </select>
      </div>
      <p className="menu__hint">{langPreferenceHint(value)}</p>
    </div>
  );
}

/** Model and reasoning effort of new summaries; "Standard" names what the account would use anyway. */
function ModelSettings({ settings, onChange, onLoad }: {
  settings: Settings;
  onChange: (patch: Pick<Settings, 'model' | 'effort'>) => void;
  onLoad: () => Promise<CodexModelsResponse>;
}) {
  const modelId = useId();
  const effortId = useId();
  const [list, setList] = useState<CodexModelsResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    onLoad().then(
      (result) => {
        if (!active) return;
        setList(result);
        setFailed(result.models.length === 0);
      },
      () => {
        if (active) setFailed(true);
      },
    );
    return () => {
      active = false;
    };
  }, [onLoad]);

  const models = list?.models ?? [];
  const known = models.length > 0;
  const defaultModel = models.find((m) => m.slug === list?.defaultModel);
  // A stored choice the account no longer offers: the server uses the default, so show that.
  const chosen = models.find((m) => m.slug === settings.model);
  const current = chosen ?? defaultModel;
  const modelValue = known && !chosen ? '' : (settings.model ?? '');
  const effortKnown = !known || !current || current.efforts.includes(settings.effort ?? '');
  const effortValue = effortKnown ? (settings.effort ?? '') : '';
  const efforts = known ? (current?.efforts ?? []) : settings.effort ? [settings.effort] : [];
  const defaultModelName = defaultModel?.name ?? list?.defaultModel;

  return (
    <div className="menu__section model-pref">
      <p className="menu__label">KI-Modell</p>
      <div className="setting-row">
        <label htmlFor={modelId} className="setting-row__label">
          Modell
        </label>
        <select
          id={modelId}
          className="select"
          value={modelValue}
          onChange={(event) => {
            const model = event.target.value || undefined;
            const next = models.find((m) => m.slug === (model ?? list?.defaultModel));
            // Keep the effort only where the newly chosen model has it.
            const effort = settings.effort && next?.efforts.includes(settings.effort) ? settings.effort : undefined;
            onChange({ model, effort });
          }}
        >
          <option value="">{defaultModelName ? `Standard (${defaultModelName})` : 'Standard'}</option>
          {!known && settings.model && <option value={settings.model}>{settings.model}</option>}
          {models.map((model) => (
            <option key={model.slug} value={model.slug}>
              {model.name}
            </option>
          ))}
        </select>
      </div>
      <div className="setting-row">
        <label htmlFor={effortId} className="setting-row__label">
          Denkaufwand
        </label>
        <select
          id={effortId}
          className="select"
          value={effortValue}
          onChange={(event) => onChange({ model: settings.model, effort: event.target.value || undefined })}
        >
          <option value="">{current?.defaultEffort ? `Standard (${effortName(current.defaultEffort)})` : 'Standard'}</option>
          {efforts.map((effort) => (
            <option key={effort} value={effort}>
              {effortName(effort)}
            </option>
          ))}
        </select>
      </div>
      <p className="menu__hint">
        {failed
          ? 'Die Modelle deines Kontos konnten gerade nicht geladen werden.'
          : 'Gilt für neue Zusammenfassungen; vorhandene bleiben, bis du sie neu erstellst. Mehr Denkaufwand dauert länger und verbraucht mehr von deinem ChatGPT-Kontingent.'}
      </p>
    </div>
  );
}

const NOTIFY_OPTIONS = [
  ['on', 'An'],
  ['off', 'Aus'],
] as const;

function NotificationSettings({ state, enabled, onEnable, onChange }: {
  state: NotificationState;
  enabled: boolean;
  onEnable: () => void;
  onChange: (notify: boolean) => void;
}) {
  if (state === 'unsupported') return null;
  return (
    <div className="menu__section">
      {state === 'granted' ? (
        <Segmented<'on' | 'off'>
          label="Benachrichtigen, wenn bereit"
          value={enabled ? 'on' : 'off'}
          options={NOTIFY_OPTIONS}
          onChange={(value) => onChange(value === 'on')}
        />
      ) : (
        <>
          <p className="menu__label">Benachrichtigungen</p>
          {state === 'default' && (
            <button type="button" className="btn btn--secondary btn--sm btn--block" onClick={onEnable}>
              <Bell size={17} aria-hidden="true" />
              Benachrichtigen, wenn bereit
            </button>
          )}
        </>
      )}
      <p className="menu__hint">
        {state === 'denied'
          ? 'Benachrichtigungen sind für diese Seite blockiert. Du kannst sie in den Browser-Einstellungen erlauben.'
          : 'Sobald eine Zusammenfassung bereit ist, während du in einer anderen App bist.'}
      </p>
    </div>
  );
}

function SettingsMenu({
  user,
  codex,
  settings,
  onSettingsChange,
  onLoadModels,
  historyCount,
  onClearHistory,
  canInstall,
  onInstall,
  notifications,
  onEnableNotifications,
}: HeaderProps) {
  return (
    <Popover
      label="Einstellungen"
      className="settings-popover"
      trigger={(triggerProps) => (
        <button type="button" {...triggerProps} className="icon-btn" aria-label="Einstellungen" title="Einstellungen">
          <SettingsIcon size={20} aria-hidden="true" />
        </button>
      )}
    >
      {(close) => (
        <div className="menu menu--settings">
          <LanguageSettings value={settings.langPref} onChange={(langPref) => onSettingsChange({ langPref })} />
          {codex.connected && <ModelSettings settings={settings} onChange={onSettingsChange} onLoad={onLoadModels} />}
          {user && (
            <div className="menu__section">
              <Segmented<TranscriptMode>
                label="Transkript laden"
                value={settings.transcriptMode}
                options={MODE_OPTIONS}
                onChange={(transcriptMode) => onSettingsChange({ transcriptMode })}
              />
              <p className="menu__hint">
                {settings.transcriptMode === 'server'
                  ? 'Der Server lädt die Untertitel von YouTube.'
                  : 'Dein Browser lädt die Untertitel direkt von YouTube.'}
              </p>
            </div>
          )}
          <NotificationSettings
            state={notifications}
            enabled={settings.notify}
            onEnable={onEnableNotifications}
            onChange={(notify) => onSettingsChange({ notify })}
          />
          {historyCount > 0 && (
            <div className="menu__section">
              <p className="menu__label">Verlauf</p>
              <p className="menu__hint">{historyCount} Zusammenfassungen, nur in diesem Browser gespeichert.</p>
              <button type="button" className="btn btn--secondary btn--sm btn--block" onClick={onClearHistory}>
                <Trash2 size={17} aria-hidden="true" />
                Verlauf löschen
              </button>
            </div>
          )}
          {canInstall && (
            <div className="menu__section">
              <button
                type="button"
                className="btn btn--primary btn--sm btn--block"
                onClick={() => {
                  close();
                  onInstall();
                }}
              >
                <Download size={17} aria-hidden="true" />
                App installieren
              </button>
              <p className="menu__hint">Dann kannst du Videos direkt aus der YouTube-App teilen.</p>
            </div>
          )}
        </div>
      )}
    </Popover>
  );
}

function TabsMenu({ tabs, currentVideoId, tabStatus, onOpenTab, onCloseTab, onCloseAllTabs }: HeaderProps) {
  const count = tabs.length;
  const busy = tabs.some((tab) => {
    const status = tabStatus(tab.videoId);
    return status === 'running' || status === 'waiting';
  });
  const unread = tabs.some((tab) => tabStatus(tab.videoId) === 'unread');
  return (
    <Popover
      label="Offene Tabs"
      className="tabs-popover"
      trigger={(triggerProps) => (
        <button
          type="button"
          {...triggerProps}
          className={`icon-btn tabs-btn${unread ? ' has-unread' : ''}`}
          aria-label={`Offene Tabs: ${count}${busy ? ', Zusammenfassung läuft' : ''}${unread ? ', neue fertig' : ''}`}
          title="Offene Tabs"
        >
          <Layers size={20} aria-hidden="true" />
          <span className={`tabs-btn__count${busy ? ' is-busy' : ''}`} aria-hidden="true">
            {count > 99 ? '99+' : count}
          </span>
        </button>
      )}
    >
      {(close) => (
        <div className="menu menu--tabs">
          <p className="menu__title menu__title--tabs">
            Offene Tabs <span className="menu__count">{count}</span>
          </p>
          <TabList
            label="Offene Tabs"
            className="tab-list--menu"
            tabs={tabs}
            currentId={currentVideoId}
            status={tabStatus}
            onOpen={(tab) => {
              close();
              onOpenTab(tab);
            }}
            onClose={(tab) => {
              // The menu disappears with the last tab: return focus to the trigger first.
              if (count === 1) close();
              onCloseTab(tab);
            }}
          />
          {count > 1 && (
            <div className="menu__foot">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  close();
                  onCloseAllTabs();
                }}
              >
                <X size={16} aria-hidden="true" />
                Alle schließen
              </button>
            </div>
          )}
        </div>
      )}
    </Popover>
  );
}

function AccountMenu({ user, onSignOut }: { user: AppUser; onSignOut: () => void }) {
  const initial = (user.name || user.email).trim().charAt(0).toUpperCase() || '?';
  return (
    <Popover
      label="Konto"
      trigger={(triggerProps) => (
        <button type="button" {...triggerProps} className="avatar" aria-label={`Konto: ${user.email}`} title={user.email}>
          {initial}
        </button>
      )}
    >
      {(close) => (
        <div className="menu">
          <p className="menu__title">{user.name || 'Angemeldet'}</p>
          <p className="menu__text menu__text--email">{user.email}</p>
          <button
            type="button"
            className="btn btn--secondary btn--sm btn--block"
            onClick={() => {
              close();
              onSignOut();
            }}
          >
            <LogOut size={17} aria-hidden="true" />
            Abmelden
          </button>
        </div>
      )}
    </Popover>
  );
}
